import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiXCircle, FiClock, FiFilter, FiTruck, FiRefreshCw, FiAlertCircle, FiMapPin, FiHome, FiPackage, FiNavigation, FiSettings, FiUser, FiMail, FiPhone, FiCalendar, FiTrendingUp, FiActivity, FiBarChart2, FiPieChart, FiTarget, FiZap, FiGlobe, FiStar, FiCreditCard, FiShoppingCart, FiDollarSign, FiDownload, FiUpload, FiEdit2, FiTrash2, FiEye2, FiPlus, FiMinus, FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { socket } from '../../socket/socket';
import { useAuth } from '../../context/AuthContext';

const DeliverySettingRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  useEffect(() => {
    if (!user || user.role !== 'superadmin') return;

    // Socket room join is handled globally in Sidebar.js
    // Listen for new delivery requests
    socket.on('deliveryRequest:new', (payload) => {
      toast.success('New delivery request received');
      fetchRequests();
    });

    // Listen for status updates (approve/reject)
    socket.on('deliveryRequest:statusUpdated', (payload) => {
      fetchRequests();
    });

    return () => {
      socket.off('deliveryRequest:new');
      socket.off('deliveryRequest:statusUpdated');
    };
  }, [user, statusFilter]);

  const fetchRequests = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
        toast.loading('Refreshing delivery requests...', { id: 'refresh' });
      } else {
        setLoading(true);
      }

      console.log('Fetching delivery requests with status:', statusFilter);
      const response = await api.get(`/superadmin/delivery-setting-requests?status=${statusFilter}`);
      console.log('Delivery requests response:', response.data);
      
      setRequests(response.data.data);
      
      // Show success message for manual refresh
      if (isManualRefresh) {
        toast.success(`Refreshed! Found ${response.data.data.length} requests`, { id: 'refresh' });
      }
    } catch (error) {
      console.error('Fetch delivery requests error:', error);
      toast.error(`Failed to fetch requests: ${error.response?.data?.message || error.message}`, { id: 'refresh' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setModalAction('approve');
    setShowModal(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setModalAction('reject');
    setShowModal(true);
  };

  const handleSubmitAction = async () => {
    if (modalAction === 'reject' && !reviewNotes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      const endpoint = modalAction === 'approve' 
        ? `/superadmin/delivery-setting-requests/${selectedRequest._id}/approve`
        : `/superadmin/delivery-setting-requests/${selectedRequest._id}/reject`;

      await api.post(endpoint, { reviewNotes });
      toast.success(`Request ${modalAction}d successfully`);
      setShowModal(false);
      setReviewNotes('');
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${modalAction} request`);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bgClass: 'bg-amber-100', textClass: 'text-amber-800', icon: FiClock },
      approved: { bgClass: 'bg-green-100', textClass: 'text-green-800', icon: FiCheckCircle },
      rejected: { bgClass: 'bg-red-100', textClass: 'text-red-800', icon: FiXCircle }
    };

    const style = styles[status] || styles.pending;
    const Icon = style.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${style.bgClass} ${style.textClass}`}>
        <Icon size={14} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-flex items-center justify-center">
            <div className="w-20 h-20 border-4 border-indigo-200 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-indigo-600 rounded-full animate-spin absolute border-t-transparent"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Loading Delivery Requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
                  <FiTruck className="text-white text-xl" />
                </div>
                Delivery Setting Requests
              </h1>
              <p className="text-gray-600 mt-1">Manage vendor requests for delivery, pickup, vehicle, and room service setting changes</p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold flex items-center gap-2">
                <FiAlertCircle className="text-blue-600" />
                {requests.length} Requests
              </div>
              <button
                onClick={() => fetchRequests(true)}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                <FiRefreshCw className={`text-sm ${refreshing ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Table Section */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FiFilter className="text-blue-600" />
              Delivery Requests
            </h3>
            <div className="flex gap-3 items-center">
              <div className="px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-2">
                <FiFilter className="text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium"
                >
                  <option value="all">All Requests</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>

        {requests.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiFilter className="text-gray-400 text-2xl" />
            </div>
            <p className="text-gray-500">No requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Restaurant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Owner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Requested For</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Radius (km)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {requests.map((request) => (
                  <tr key={request._id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">{request.vendor?.restaurantDetails?.restaurantName || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">
                          {request.vendor?.personalDetails?.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-600">
                          {request.vendor?.personalDetails?.email || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-900">
                        {request.settingType === 'homeDelivery' ? 'Home Delivery' : 
                         request.settingType === 'pickup' ? 'Pickup' : 
                         request.settingType === 'vehicle' ? 'Vehicle' : 
                         'Room Service'}
                      </div>
                      <div className="mt-1">
                        <span className={`text-sm font-bold ${
                          request.requestedValue ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {request.requestedValue ? 'Enable' : 'Disable'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-700">{request.deliveryRadius || 'N/A'}</td>
                    <td className="px-6 py-4">{getStatusBadge(request.status)}</td>
                    <td className="px-6 py-4 text-gray-700">{formatDate(request.createdAt)}</td>
                    <td className="px-6 py-4">
                      {request.status === 'pending' ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApprove(request)}
                            className="group relative px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg flex items-center gap-2"
                          >
                            <FiCheckCircle size={14} className="group-hover:scale-110 transition-transform" />
                            <span>Approve</span>
                            <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                          </button>
                          <button
                            onClick={() => handleReject(request)}
                            className="group relative px-3 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg hover:from-red-600 hover:to-pink-600 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg flex items-center gap-2"
                          >
                            <FiXCircle size={14} className="group-hover:scale-110 transition-transform" />
                            <span>Reject</span>
                            <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div className="text-sm text-gray-600">
                            {request.reviewedAt && `Reviewed: ${formatDate(request.reviewedAt)}`}
                          </div>
                          {request.reviewNotes && (
                            <div className="mt-1 text-sm text-gray-600">
                              <strong>Notes:</strong> {request.reviewNotes}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                {modalAction === 'approve' ? 'Approve' : 'Reject'} Request
              </h3>
            </div>

            <div className="p-6">
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="mb-2">
                  <strong className="text-gray-700">Restaurant:</strong> {selectedRequest.vendor?.restaurantDetails?.restaurantName || 'N/A'}
                </div>
                <div className="mb-2">
                  <strong className="text-gray-700">Owner:</strong> {selectedRequest.vendor?.personalDetails?.name || 'N/A'}
                </div>
                <div>
                  <strong className="text-gray-700">Requested For:</strong> {selectedRequest.settingType === 'homeDelivery' ? 'Home Delivery' : selectedRequest.settingType === 'pickup' ? 'Pickup' : selectedRequest.settingType === 'vehicle' ? 'Vehicle' : 'Room Service'}
                </div>
              </div>

              {modalAction === 'reject' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Rejection *
                  </label>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Please provide a reason for rejection..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-vertical"
                    rows="4"
                  />
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={handleSubmitAction}
                className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-white transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2 ${
                  modalAction === 'approve' 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600' 
                    : 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600'
                }`}
              >
                {modalAction === 'approve' ? <FiCheckCircle className="group-hover:scale-110 transition-transform" /> : <FiXCircle className="group-hover:scale-110 transition-transform" />}
                {modalAction === 'approve' ? 'Approve' : 'Reject'}
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  setReviewNotes('');
                  setSelectedRequest(null);
                }}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg font-medium hover:from-gray-600 hover:to-gray-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Cancel
              </button>
            </div>
          </div>
      </div>

        )}
      </div>
    </div>
  );
};

export default DeliverySettingRequests;
